import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, MapPin, Clock, Users, Shield, Edit, Loader2, X, Briefcase, ChevronLeft, ChevronRight, Sparkles, DollarSign, Camera, Video, Scissors, Image, Headphones, Navigation, Mic, Heart, Building2, PartyPopper, Home, MoreHorizontal, Check, FileText } from 'lucide-react';
import axios from 'axios';
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { useTheme } from '@/contexts/ThemeContext';
import "@/styles/commandDeck.css";

const PostJobUltraModern = ({ onLogout }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [userInput, setUserInput] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    dates: [''],
    location: '',
    description: '',
    crewCount: 1,
    crewMembers: [
      {
        role: '',
        id: 1,
        dateAssignments: [
          { dateIndex: 0, hours: '', ratePerHour: '' }
        ]
      }
    ],
    requiresWWCC: false,
    jobType: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedJobId, setSubmittedJobId] = useState(null);
  const [nearbyCrewCount, setNearbyCrewCount] = useState(null);

  const calculateTotalBudget = () => {
    return formData.crewMembers.reduce((total, member) => {
      const memberTotal = member.dateAssignments.reduce((sum, assignment) => {
        const hours = parseFloat(assignment.hours) || 0;
        const rate = parseFloat(assignment.ratePerHour) || 0;
        return sum + (hours * rate);
      }, 0);
      return total + memberTotal;
    }, 0).toFixed(2);
  };

  const calculateTotalHours = () => {
    return formData.crewMembers.reduce((total, member) => {
      const memberHours = member.dateAssignments.reduce((sum, assignment) => {
        return sum + (parseFloat(assignment.hours) || 0);
      }, 0);
      return total + memberHours;
    }, 0);
  };

  const totalBudget = calculateTotalBudget();
  const totalHours = calculateTotalHours();

  useEffect(() => {
    let mounted = true;

    loadGoogleMapsScript(() => {
      setTimeout(() => {
        if (!mounted) return;
        const container = document.getElementById('job-location-autocomplete');
        if (!container) return;

        try {
          initPlaceAutocomplete(
            'job-location-autocomplete',
            (place) => {
              if (place && place.formatted_address) {
                setFormData(prev => ({ ...prev, location: place.formatted_address }));
                setLocationSelected(true);
                if (place.lat && place.lng) {
                  setSelectedLocation({ lat: place.lat, lng: place.lng });
                }
              }
            }
          );
        } catch (error) {
          console.error('Error initializing autocomplete:', error);
        }
      }, 1500);
    });

    return () => {
      mounted = false;
      try { destroyPlaceAutocomplete('job-location-autocomplete'); } catch (e) {}
    };
  }, []);

  const handleCrewCountChange = (count) => {
    const newCount = parseInt(count) || 1;
    const currentMembers = formData.crewMembers;

    if (newCount > currentMembers.length) {
      const newMembers = [...currentMembers];
      for (let i = currentMembers.length; i < newCount; i++) {
        newMembers.push({
          role: '',
          id: i + 1,
          dateAssignments: formData.dates.map((_, dateIndex) => ({
            dateIndex, hours: '', ratePerHour: ''
          }))
        });
      }
      setFormData(prev => ({ ...prev, crewCount: newCount, crewMembers: newMembers }));
    } else if (newCount < currentMembers.length) {
      setFormData(prev => ({ ...prev, crewCount: newCount, crewMembers: currentMembers.slice(0, newCount) }));
    } else {
      setFormData(prev => ({ ...prev, crewCount: newCount }));
    }
  };

  const removeDateAssignmentForMember = (memberIndex, dateIndex) => {
    setFormData(prev => {
      const newMembers = [...prev.crewMembers];
      const member = { ...newMembers[memberIndex] };
      member.dateAssignments = member.dateAssignments.filter(a => a.dateIndex !== dateIndex);
      newMembers[memberIndex] = member;
      return { ...prev, crewMembers: newMembers };
    });
  };

  const addDateAssignmentForMember = (memberIndex, dateIndex) => {
    setFormData(prev => {
      const newMembers = [...prev.crewMembers];
      const member = { ...newMembers[memberIndex] };
      member.dateAssignments = [...member.dateAssignments, { dateIndex, hours: '', ratePerHour: '' }];
      newMembers[memberIndex] = member;
      return { ...prev, crewMembers: newMembers };
    });
  };

  const handleCrewMemberChange = (memberIndex, field, value) => {
    const newMembers = [...formData.crewMembers];
    newMembers[memberIndex] = { ...newMembers[memberIndex], [field]: value };
    setFormData(prev => ({ ...prev, crewMembers: newMembers }));
  };

  const handleDateAssignmentChange = (memberIndex, dateIndex, field, value) => {
    const newMembers = [...formData.crewMembers];
    const member = { ...newMembers[memberIndex] };
    const newAssignments = [...member.dateAssignments];

    let assignmentIndex = newAssignments.findIndex(a => a.dateIndex === dateIndex);
    if (assignmentIndex === -1) {
      newAssignments.push({ dateIndex, hours: '', ratePerHour: '' });
      assignmentIndex = newAssignments.length - 1;
    }

    newAssignments[assignmentIndex] = { ...newAssignments[assignmentIndex], [field]: value };
    member.dateAssignments = newAssignments;
    newMembers[memberIndex] = member;
    setFormData(prev => ({ ...prev, crewMembers: newMembers }));
  };

  const generateJobBrief = async () => {
    if (!userInput || userInput.trim().length < 10) {
      alert('Please enter at least a few key details about the job (minimum 10 characters)');
      return;
    }
    setGeneratingBrief(true);
    setShowAIModal(false);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/generate-job-brief`,
        {
          title: formData.title,
          roles: formData.crewMembers.filter(m => m.role).map(m => m.role),
          location: formData.location,
          userInput: userInput.trim()
        }
      );
      if (response.data.brief) {
        setFormData(prev => ({ ...prev, description: response.data.brief }));
        setUserInput('');
      }
    } catch (error) {
      alert(`Failed to generate brief: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingBrief(false);
    }
  };

  const openAIModal = () => {
    if (!formData.title || formData.title.trim().length < 3) {
      alert('Please enter a job title first so AI knows what to write about!');
      return;
    }
    setShowAIModal(true);
  };

  const addDate = () => {
    const newDateIndex = formData.dates.length;
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, ''],
      crewMembers: prev.crewMembers.map(member => ({
        ...member,
        dateAssignments: [...member.dateAssignments, { dateIndex: newDateIndex, hours: '', ratePerHour: '' }]
      }))
    }));
  };

  const removeDate = (index) => {
    if (formData.dates.length > 1) {
      setFormData(prev => ({
        ...prev,
        dates: prev.dates.filter((_, i) => i !== index),
        crewMembers: prev.crewMembers.map(member => ({
          ...member,
          dateAssignments: member.dateAssignments
            .filter(a => a.dateIndex !== index)
            .map(a => ({ ...a, dateIndex: a.dateIndex > index ? a.dateIndex - 1 : a.dateIndex }))
        }))
      }));
    }
  };

  const updateDate = (index, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => i === index ? value : date)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step >= 1) {
      if (!formData.title.trim()) newErrors.title = 'Job title is required';
      if (!formData.jobType) newErrors.jobType = 'Please select a job type';
      if (!formData.location.trim()) newErrors.location = 'Location is required';
      const validDates = formData.dates.filter(d => d.trim());
      if (validDates.length === 0) newErrors.dates = 'At least one date is required';
    }
    if (step >= 2) {
      const invalidCrew = formData.crewMembers.filter(member => {
        if (!member.role) return true;
        return !member.dateAssignments.some(a =>
          a.hours && parseFloat(a.hours) > 0 && a.ratePerHour && parseFloat(a.ratePerHour) > 0
        );
      });
      if (invalidCrew.length > 0) newErrors.crewMembers = 'All crew members need a role with hours and rate';
    }
    if (step >= 3) {
      if (!formData.description.trim()) newErrors.description = 'Please add a job description';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Job title is required';
    const validDates = formData.dates.filter(d => d.trim());
    if (validDates.length === 0) newErrors.dates = 'At least one date is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.jobType) newErrors.jobType = 'Job type is required';

    const invalidCrew = formData.crewMembers.filter(member => {
      if (!member.role) return true;
      return !member.dateAssignments.some(a =>
        a.hours && parseFloat(a.hours) > 0 && a.ratePerHour && parseFloat(a.ratePerHour) > 0
      );
    });
    if (invalidCrew.length > 0) {
      newErrors.crewMembers = 'All crew members must have a role and at least one date with hours and rate assigned';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const googleInput = document.querySelector('#job-location-autocomplete input');
    if (googleInput && googleInput.value && !formData.location) {
      setFormData(prev => ({ ...prev, location: googleInput.value }));
      setLocationSelected(true);
      if (window.google && window.google.maps) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: googleInput.value, componentRestrictions: { country: 'AU' } }, (results, status) => {
              if (status === 'OK' && results && results[0]) resolve(results[0]);
              else reject(new Error(`Geocoding failed: ${status}`));
            });
          });
          const location = result.geometry.location;
          setSelectedLocation({ lat: location.lat(), lng: location.lng() });
        } catch (error) { /* proceed without coordinates */ }
      }
      setTimeout(() => handleSubmit(e), 200);
      return;
    }

    if (formData.location && !selectedLocation && window.google && window.google.maps) {
      setLoading(true);
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: formData.location, componentRestrictions: { country: 'AU' } }, (results, status) => {
            if (status === 'OK' && results && results[0]) resolve(results[0]);
            else reject(new Error(`Geocoding failed: ${status}`));
          });
        });
        const location = result.geometry.location;
        setSelectedLocation({ lat: location.lat(), lng: location.lng() });
      } catch (error) { /* proceed */ }
      setLoading(false);
    }

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    try {
      const userStr = localStorage.getItem('shootzu_user');
      if (!userStr) { alert('Please log in to post a job'); window.location.href = '/login'; return; }
      const user = JSON.parse(userStr);
      const validDates = formData.dates.filter(d => d.trim());
      const required_roles = {};
      formData.crewMembers.forEach(member => { if (member.role) required_roles[member.role] = true; });

      const jobData = {
        title: formData.title,
        date: validDates[0],
        additional_dates: validDates.slice(1),
        location: formData.location,
        latitude: selectedLocation?.lat || null,
        longitude: selectedLocation?.lng || null,
        budget: parseFloat(totalBudget),
        description: formData.description,
        crewCount: formData.crewCount,
        crewMembers: formData.crewMembers.map(member => {
          const totalHrs = member.dateAssignments.reduce((s, a) => s + (parseFloat(a.hours) || 0), 0);
          const activeEntries = member.dateAssignments.filter(a => (parseFloat(a.hours) || 0) > 0 && (parseFloat(a.ratePerHour) || 0) > 0);
          const avgRate = activeEntries.length > 0
            ? activeEntries.reduce((s, a) => s + (parseFloat(a.ratePerHour) || 0), 0) / activeEntries.length : 0;
          const subtotal = member.dateAssignments.reduce((s, a) =>
            s + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)), 0);
          return {
            role: member.role,
            hours: totalHrs,
            ratePerHour: avgRate,
            subtotal,
            dateAssignments: member.dateAssignments.map(a => ({
              date: validDates[a.dateIndex],
              hours: parseFloat(a.hours) || 0,
              ratePerHour: parseFloat(a.ratePerHour) || 0,
              subtotal: (parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)
            }))
          };
        }),
        required_roles,
        requiresWWCC: formData.requiresWWCC,
        client_email: user.email,
        client_id: user.id,
        client_name: user.full_name || user.email,
        job_type: formData.jobType,
        status: 'PENDING_APPROVAL'
      };

      const token = localStorage.getItem('shootzu_token');
      if (!token) { alert('Please log in to post a job'); window.location.href = '/login'; return; }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs`, jobData,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      setSubmittedJobId(response.data.id || response.data._id);

      if (selectedLocation?.lat && selectedLocation?.lng) {
        try {
          const crewCountResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/crew-count-near-location`,
            { params: { lat: selectedLocation.lat, lng: selectedLocation.lng, radius: 200 } }
          );
          if (crewCountResponse.data?.crew_count !== undefined) {
            setNearbyCrewCount(crewCountResponse.data.crew_count);
          }
        } catch (e) { /* ignore */ }
      }

      setShowConfirmation(true);
    } catch (error) {
      alert(`Failed to post job: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const jobTypeOptions = [
    { value: 'WEDDINGS', label: 'Wedding' },
    { value: 'CORPORATE', label: 'Corporate' },
    { value: 'EVENTS', label: 'Events' },
    { value: 'REAL_ESTATE', label: 'Real Estate' },
    { value: 'OTHERS', label: 'Other' }
  ];

  const roleOptions = [
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'editor_video', label: 'Editor (Video)' },
    { value: 'editor_photos', label: 'Editor (Photos)' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'drone_operator', label: 'Drone Operator' }
  ];

  const jobTypeCards = [
    { value: 'WEDDINGS', label: 'Wedding', icon: Heart, color: '#ec4899' },
    { value: 'CORPORATE', label: 'Corporate', icon: Building2, color: '#3b82f6' },
    { value: 'EVENTS', label: 'Events', icon: PartyPopper, color: '#f59e0b' },
    { value: 'REAL_ESTATE', label: 'Real Estate', icon: Home, color: '#10b981' },
    { value: 'OTHERS', label: 'Other', icon: MoreHorizontal, color: '#8b5cf6' },
  ];

  const roleCards = [
    { value: 'photographer', label: 'Photographer', icon: Camera, color: '#3b82f6' },
    { value: 'videographer', label: 'Videographer', icon: Video, color: '#ec4899' },
    { value: 'editor_video', label: 'Video Editor', icon: Scissors, color: '#f59e0b' },
    { value: 'editor_photos', label: 'Photo Editor', icon: Image, color: '#10b981' },
    { value: 'assistant', label: 'Assistant', icon: Headphones, color: '#8b5cf6' },
    { value: 'drone_operator', label: 'Drone Operator', icon: Navigation, color: '#06b6d4' },
    { value: 'voxpop_videographer', label: 'VoxPop Videographer', icon: Mic, color: '#ef4444' },
  ];

  const steps = [
    { num: 1, label: 'Job Details', icon: Briefcase },
    { num: 2, label: 'Crew', icon: Users },
    { num: 3, label: 'Brief', icon: FileText },
    { num: 4, label: 'Review', icon: Check },
  ];

  // ── Command Deck style tokens (inline) ──────────────────────────────────────
  const cardStyle = {
    background: 'rgba(255,255,255,.045)',
    border: '1px solid rgba(255,255,255,.09)',
    borderRadius: 16,
    padding: '24px 26px',
  };
  const fieldLabel = { display: 'block', fontSize: 12, color: 'rgba(255,255,255,.55)', marginBottom: 7 };
  const inputStyle = { padding: '12px 14px', fontSize: 14 };
  const errStyle = { color: '#FCA5A5', fontSize: 12, marginTop: 6, fontWeight: 500 };
  const ghostBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px', borderRadius: 12, background: 'transparent',
    border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontWeight: 600, fontSize: 14,
    cursor: 'pointer', fontFamily: "'Work Sans', sans-serif",
  };
  const primaryBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 22px', borderRadius: 12, background: '#28C0D0', border: 'none',
    color: '#0C1120', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif", boxShadow: '0 12px 28px -10px rgba(40,192,208,.6)',
  };
  const stepTitle = { fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: 21, color: '#fff', letterSpacing: '-.5px', marginBottom: 4 };
  const stepSub = { fontSize: 13, color: 'rgba(255,255,255,.6)', marginBottom: 18 };

  const Chip = ({ active, onClick, children, small }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: small ? '8px 13px' : '9px 15px', borderRadius: 999,
        border: active ? '1px solid #28C0D0' : '1px solid rgba(255,255,255,.14)',
        background: active ? '#28C0D0' : 'rgba(255,255,255,.05)',
        color: active ? '#0C1120' : 'rgba(255,255,255,.75)',
        fontFamily: "'Work Sans', sans-serif", fontWeight: 600, fontSize: small ? 12 : 12.5, cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );

  // Progress indicator component
  const ProgressBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26, padding: '0 4px' }}>
      {steps.map((step, i) => {
        const done = step.num < currentStep;
        const active = step.num === currentStep;
        return (
          <React.Fragment key={step.num}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <button
                type="button"
                onClick={() => { if (step.num < currentStep) goToStep(step.num); }}
                style={{
                  width: 38, height: 38, borderRadius: 999, border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 14,
                  cursor: done ? 'pointer' : 'default',
                  ...(done
                    ? { background: 'rgba(103,198,180,.18)', color: '#67C6B4' }
                    : active
                      ? { background: '#28C0D0', color: '#0C1120', boxShadow: '0 10px 24px -10px rgba(40,192,208,.6)' }
                      : { background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.4)' }),
                }}
              >
                {done ? <Check className="w-5 h-5" /> : step.num}
              </button>
              <span style={{
                fontSize: 11, marginTop: 8, fontWeight: 600, textAlign: 'center',
                color: active ? '#fff' : done ? '#67C6B4' : 'rgba(255,255,255,.4)',
              }}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '0 4px', marginTop: -20, borderRadius: 999,
                background: done ? '#67C6B4' : 'rgba(255,255,255,.12)',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Navigation buttons
  const NavButtons = ({ showSubmit = false }) => (
    <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
      {currentStep > 1 && (
        <button type="button" onClick={goBack} style={ghostBtn}>
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
      )}
      {currentStep === 1 && (
        <button type="button" onClick={() => navigate('/hire-crew')} style={ghostBtn}>
          Cancel
        </button>
      )}
      <div style={{ flex: 1 }} />
      {showSubmit ? (
        <button type="submit" disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.6 : 1 }}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting...</> : <><Check className="w-4 h-4" /> Post job</>}
        </button>
      ) : (
        <button type="button" onClick={goNext} style={primaryBtn}>
          Next <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header onLogout={onLogout} />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <main className="cd-main" style={{ maxWidth: 640, position: 'relative', zIndex: 1 }}>

          {/* Page Header */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>POST A JOB</div>
            <h1 className="cd-display" style={{ fontWeight: 800, fontSize: 30, color: '#fff', letterSpacing: '-.5px', marginTop: 6 }}>
              {steps[currentStep - 1].label}
            </h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
              Step {currentStep} of 4
            </p>
          </div>

          {/* Progress Bar */}
          <ProgressBar />

          {/* Form */}
          <form onSubmit={handleSubmit}>

            {/* ═══════ STEP 1: JOB DETAILS ═══════ */}
            {currentStep === 1 && (
              <div style={cardStyle}>
                <div style={stepTitle}>What's the job?</div>
                <div style={stepSub}>Start with the basics — you can refine details next.</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Title */}
                  <div>
                    <label style={fieldLabel}>Job title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Wedding Photographer · Fitzroy"
                      className="cd-input"
                      style={inputStyle}
                      autoFocus
                    />
                    {errors.title && <p style={errStyle}>{errors.title}</p>}
                  </div>

                  {/* Job Type - Chips */}
                  <div>
                    <label style={fieldLabel}>Job type *</label>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      {jobTypeCards.map(type => {
                        const Icon = type.icon;
                        const isSelected = formData.jobType === type.value;
                        return (
                          <Chip key={type.value} active={isSelected} onClick={() => setFormData(prev => ({ ...prev, jobType: type.value }))}>
                            <Icon className="w-3.5 h-3.5" />
                            {type.label}
                          </Chip>
                        );
                      })}
                    </div>
                    {errors.jobType && <p style={errStyle}>{errors.jobType}</p>}
                  </div>

                  {/* Location */}
                  <div>
                    <label style={fieldLabel}>
                      <MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                      Location *
                    </label>
                    <div id="job-location-autocomplete" className="mb-2"></div>
                    {formData.location && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, fontSize: 13,
                        background: 'rgba(103,198,180,.08)', border: '1px solid rgba(103,198,180,.25)', color: '#67C6B4',
                      }}>
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formData.location}</span>
                        {selectedLocation && <Check className="w-4 h-4 ml-auto flex-shrink-0" style={{ color: '#67C6B4' }} />}
                      </div>
                    )}
                    {errors.location && <p style={errStyle}>{errors.location}</p>}
                  </div>

                  {/* Event Dates */}
                  <div>
                    <label style={fieldLabel}>When is the job? *</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {formData.dates.map((date, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <Calendar className="w-4 h-4" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,.5)', pointerEvents: 'none', zIndex: 10 }} />
                            <input
                              type="date"
                              value={date}
                              onChange={(e) => updateDate(index, e.target.value)}
                              className="cd-input"
                              style={{ ...inputStyle, paddingLeft: 38, colorScheme: 'dark' }}
                            />
                          </div>
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => removeDate(index)}
                              style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12, flexShrink: 0, cursor: 'pointer', background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.25)', color: '#FCA5A5' }}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addDate}
                      style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.7)' }}
                    >
                      <Plus className="w-3.5 h-3.5" /> Add another date
                    </button>
                    {errors.dates && <p style={errStyle}>{errors.dates}</p>}
                  </div>
                </div>

                <NavButtons />
              </div>
            )}

            {/* ═══════ STEP 2: CREW REQUIREMENTS ═══════ */}
            {currentStep === 2 && (
              <div style={cardStyle}>
                <div style={stepTitle}>Who do you need?</div>
                <div style={stepSub}>Set how many crew, then a role &amp; rate for each.</div>

                {/* Crew Count - Stepper */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 13, background: 'rgba(255,255,255,.03)', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>Number of crew</div>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>How many do you need?</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
                    <button
                      type="button"
                      onClick={() => handleCrewCountChange(Math.max(1, formData.crewCount - 1))}
                      style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >−</button>
                    <span className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#fff', minWidth: 20, textAlign: 'center' }}>{formData.crewCount}</span>
                    <button
                      type="button"
                      onClick={() => handleCrewCountChange(Math.min(10, formData.crewCount + 1))}
                      style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(40,192,208,.16)', border: '1px solid rgba(40,192,208,.4)', color: '#28C0D0', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >+</button>
                  </div>
                </div>

                {errors.crewMembers && (
                  <div style={{ padding: 12, borderRadius: 12, marginBottom: 14, fontSize: 13, fontWeight: 500, background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.25)', color: '#FCA5A5' }}>{errors.crewMembers}</div>
                )}

                {/* Crew Member Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {formData.crewMembers.map((member, memberIndex) => {
                    const memberTotal = member.dateAssignments.reduce((sum, a) =>
                      sum + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)), 0);

                    return (
                      <div key={member.id} style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 13, padding: '14px 15px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                            <span style={{ width: 24, height: 24, borderRadius: 999, background: 'rgba(40,192,208,.16)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 11, flex: '0 0 auto' }}>
                              {memberIndex + 1}
                            </span>
                            <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 13.5, color: '#fff' }}>
                              Crew member {memberIndex + 1}
                            </span>
                          </div>
                          {memberTotal > 0 && (
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#67C6B4' }}>${memberTotal.toFixed(2)}</span>
                          )}
                        </div>

                        {/* Role - Chips */}
                        <div style={{ marginBottom: 11 }}>
                          <label style={{ ...fieldLabel, marginBottom: 8 }}>Role *</label>
                          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                            {roleCards.map(role => {
                              const Icon = role.icon;
                              const isSelected = member.role === role.value;
                              return (
                                <Chip key={role.value} active={isSelected} small onClick={() => handleCrewMemberChange(memberIndex, 'role', role.value)}>
                                  <Icon className="w-3 h-3" />
                                  {role.label}
                                </Chip>
                              );
                            })}
                          </div>
                        </div>

                        {/* Per-Date Assignments */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <label style={fieldLabel}>Hours &amp; rate per date *</label>
                          {formData.dates.map((date, dateIndex) => {
                            const assignment = member.dateAssignments.find(a => a.dateIndex === dateIndex);
                            if (!assignment) {
                              return (
                                <div key={dateIndex} style={{ borderRadius: 12, padding: 12, border: '2px dashed rgba(255,255,255,.14)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>{formatDate(date) || `Date ${dateIndex + 1}`} — not assigned</span>
                                  <button type="button" onClick={() => addDateAssignmentForMember(memberIndex, dateIndex)}
                                    style={{ padding: '5px 11px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'rgba(103,198,180,.15)', border: '1px solid rgba(103,198,180,.3)', color: '#67C6B4', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <Plus className="w-3 h-3" /> Add
                                  </button>
                                </div>
                              );
                            }
                            const subtotal = (parseFloat(assignment.hours) || 0) * (parseFloat(assignment.ratePerHour) || 0);
                            return (
                              <div key={dateIndex} style={{ borderRadius: 12, padding: 12, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.75)' }}>{formatDate(date) || `Date ${dateIndex + 1}`}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {subtotal > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: '#67C6B4' }}>${subtotal.toFixed(2)}</span>}
                                    {member.dateAssignments.length > 1 && (
                                      <button type="button" onClick={() => removeDateAssignmentForMember(memberIndex, dateIndex)}
                                        style={{ width: 24, height: 24, borderRadius: 8, background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.25)', color: '#FCA5A5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                  <div>
                                    <label style={{ ...fieldLabel, marginBottom: 6 }}>Hours</label>
                                    <input type="number" value={assignment.hours} onChange={(e) => handleDateAssignmentChange(memberIndex, dateIndex, 'hours', e.target.value)}
                                      placeholder="8" min="0" step="any" className="cd-input" style={{ ...inputStyle, textAlign: 'center', fontWeight: 700 }} />
                                  </div>
                                  <div>
                                    <label style={{ ...fieldLabel, marginBottom: 6 }}>Rate ($/hr)</label>
                                    <input type="number" value={assignment.ratePerHour} onChange={(e) => handleDateAssignmentChange(memberIndex, dateIndex, 'ratePerHour', e.target.value)}
                                      placeholder="80" min="0" step="any" className="cd-input" style={{ ...inputStyle, textAlign: 'center', fontWeight: 700 }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Budget Summary */}
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 13, background: 'rgba(103,198,180,.07)', border: '1px solid rgba(103,198,180,.22)' }}>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#67C6B4' }}>Total budget</p>
                    <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>
                      {formData.crewCount} crew &middot; {totalHours > 0 ? `${totalHours}h total` : 'No hours yet'}
                    </p>
                  </div>
                  <p className="cd-display" style={{ fontSize: 20, fontWeight: 800, color: '#67C6B4' }}>${totalBudget}</p>
                </div>

                <NavButtons />
              </div>
            )}

            {/* ═══════ STEP 3: JOB BRIEF ═══════ */}
            {currentStep === 3 && (
              <div style={cardStyle}>
                <div style={stepTitle}>Add the brief</div>
                <div style={stepSub}>Tell crew what the shoot involves, then review.</div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>Brief *</span>
                    <button
                      type="button"
                      onClick={openAIModal}
                      disabled={generatingBrief || !formData.title.trim()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600,
                        color: '#C98AD4', background: 'rgba(120,40,124,.16)', border: '1px solid rgba(120,40,124,.32)',
                        padding: '5px 11px', borderRadius: 999, cursor: (generatingBrief || !formData.title.trim()) ? 'not-allowed' : 'pointer',
                        opacity: (generatingBrief || !formData.title.trim()) ? 0.5 : 1,
                      }}
                    >
                      {generatingBrief ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</> : <><Sparkles className="w-3 h-3" /> Generate with AI</>}
                    </button>
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="The shoot, deliverables, the vibe, anything crew should know… or tap Generate with AI."
                    rows="5"
                    className="cd-input"
                    style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }}
                    autoFocus
                  />
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 7 }}>
                    AI drafts from your title, type &amp; location — you can edit before posting.
                  </div>
                  {errors.description && <p style={errStyle}>{errors.description}</p>}
                </div>

                {/* WWCC */}
                <label htmlFor="wwcc-checkbox" style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 13, cursor: 'pointer', marginTop: 16,
                  border: formData.requiresWWCC ? '1px solid rgba(103,198,180,.3)' : '1px solid rgba(255,255,255,.12)',
                  background: formData.requiresWWCC ? 'rgba(103,198,180,.07)' : 'rgba(255,255,255,.03)',
                }}>
                  <input
                    type="checkbox"
                    id="wwcc-checkbox"
                    checked={formData.requiresWWCC}
                    onChange={(e) => setFormData(prev => ({ ...prev, requiresWWCC: e.target.checked }))}
                    style={{ width: 18, height: 18, accentColor: '#28C0D0', flex: '0 0 auto' }}
                  />
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                      <Shield className="w-3.5 h-3.5 inline mr-1 -mt-0.5" style={{ color: '#67C6B4' }} />
                      Requires a valid WWCC
                    </p>
                    <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
                      Crew must have a Working with Children Check for this event
                    </p>
                  </div>
                </label>

                {/* Escrow Summary */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', border: '1px solid rgba(103,198,180,.22)', background: 'rgba(103,198,180,.07)', borderRadius: 13, marginTop: 16 }}>
                  <div>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.7)' }}>Held in escrow once hired</span>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>+ 5% platform fee on payment</div>
                  </div>
                  <span className="cd-display" style={{ fontWeight: 800, fontSize: 20, color: '#67C6B4' }}>${totalBudget}</span>
                </div>

                <NavButtons />
              </div>
            )}

            {/* ═══════ STEP 4: REVIEW & POST ═══════ */}
            {currentStep === 4 && (
              <div style={cardStyle}>
                <div style={stepTitle}>Review &amp; post</div>
                <div style={stepSub}>Check the details, then send it to crew.</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Job Details Summary */}
                  <div style={{ borderRadius: 13, padding: 15, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Job details</h3>
                      <button type="button" onClick={() => goToStep(1)} style={{ fontSize: 12, fontWeight: 600, color: '#28C0D0', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                    </div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <p><span style={{ fontWeight: 600, color: '#fff' }}>Title:</span> {formData.title}</p>
                      <p><span style={{ fontWeight: 600, color: '#fff' }}>Type:</span> {jobTypeCards.find(t => t.value === formData.jobType)?.label || '—'}</p>
                      <p><span style={{ fontWeight: 600, color: '#fff' }}>Location:</span> {formData.location}</p>
                      <p><span style={{ fontWeight: 600, color: '#fff' }}>Date{formData.dates.filter(d => d).length > 1 ? 's' : ''}:</span> {formData.dates.filter(d => d).map(d => formatDate(d)).join(', ') || '—'}</p>
                    </div>
                  </div>

                  {/* Crew Summary */}
                  <div style={{ borderRadius: 13, padding: 15, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Crew ({formData.crewCount})</h3>
                      <button type="button" onClick={() => goToStep(2)} style={{ fontSize: 12, fontWeight: 600, color: '#28C0D0', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {formData.crewMembers.map((member, i) => {
                        const roleLabel = roleCards.find(r => r.value === member.role)?.label || 'Unassigned';
                        const memberTotal = member.dateAssignments.reduce((sum, a) =>
                          sum + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)), 0);
                        const memberHours = member.dateAssignments.reduce((sum, a) => sum + (parseFloat(a.hours) || 0), 0);
                        return (
                          <div key={member.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,.04)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 22, height: 22, borderRadius: 999, background: 'rgba(40,192,208,.16)', color: '#28C0D0', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,.85)' }}>{roleLabel}</span>
                              <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.45)' }}>{memberHours}h</span>
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#67C6B4' }}>${memberTotal.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Held in escrow once hired</span>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>+ 5% platform fee on payment</div>
                      </div>
                      <span className="cd-display" style={{ fontSize: 18, fontWeight: 800, color: '#67C6B4' }}>${totalBudget}</span>
                    </div>
                  </div>

                  {/* Brief Summary */}
                  <div style={{ borderRadius: 13, padding: 15, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Brief &amp; requirements</h3>
                      <button type="button" onClick={() => goToStep(3)} style={{ fontSize: 12, fontWeight: 600, color: '#28C0D0', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                    </div>
                    <p style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,.7)' }}>
                      {formData.description.length > 200 ? formData.description.substring(0, 200) + '...' : formData.description}
                    </p>
                    {formData.requiresWWCC && (
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#67C6B4' }}>
                        <Shield className="w-3.5 h-3.5" /> WWCC Required
                      </div>
                    )}
                  </div>
                </div>

                <NavButtons showSubmit />
              </div>
            )}

          </form>
        </main>
      </div>

      {/* AI Modal */}
      {showAIModal && (
        <div className="cd-modal-overlay">
          <div className="cd-modal-card" style={{ maxWidth: 520, padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 18, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles className="w-5 h-5" style={{ color: '#C98AD4' }} /> Generate job brief
              </h2>
              <button onClick={() => setShowAIModal(false)} style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div style={{ fontSize: 12, marginBottom: 14, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.09)', color: 'rgba(255,255,255,.7)' }}>
              <span style={{ fontWeight: 600, color: '#fff' }}>Job:</span> {formData.title || 'No title set'}
              {formData.location && <span style={{ marginLeft: 8 }}>| {formData.location}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', marginBottom: 8 }}>
              Enter a short prompt about the job and what you need — AI will do the rest.
            </div>
            <textarea value={userInput} onChange={(e) => setUserInput(e.target.value)}
              placeholder="e.g. full-day wedding in Fitzroy, candid + posed, 2 shooters"
              rows="4" className="cd-input" style={{ ...inputStyle, resize: 'none', lineHeight: 1.5, marginBottom: 4 }} autoFocus />
            <p style={{ fontSize: 11, marginBottom: 18, textAlign: 'right', color: 'rgba(255,255,255,.4)' }}>
              {userInput.length} characters {userInput.length < 10 && '(minimum 10)'}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowAIModal(false)} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
              <button onClick={generateJobBrief} disabled={!userInput || userInput.trim().length < 10}
                style={{
                  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '11px 16px', borderRadius: 12, background: 'linear-gradient(120deg,#78287C,#C98AD4)', border: 'none',
                  color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: (!userInput || userInput.trim().length < 10) ? 'not-allowed' : 'pointer',
                  opacity: (!userInput || userInput.trim().length < 10) ? 0.45 : 1,
                }}>
                <Sparkles className="w-4 h-4" /> Generate brief
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="cd-modal-overlay">
          <div className="cd-modal-card" style={{ maxWidth: 440, padding: 32, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
              <div style={{ width: 64, height: 64, borderRadius: 999, background: 'rgba(103,198,180,.15)', border: '1px solid rgba(103,198,180,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Check className="w-8 h-8" style={{ color: '#67C6B4' }} />
              </div>
            </div>
            <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Job submitted!</h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginBottom: 18 }}>Your job has been sent to our admin team for approval.</p>
            {nearbyCrewCount !== null && nearbyCrewCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 13, marginBottom: 18, textAlign: 'left', background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.25)' }}>
                <Users className="w-5 h-5 flex-shrink-0" style={{ color: '#28C0D0' }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{nearbyCrewCount} crew member{nearbyCrewCount !== 1 ? 's' : ''} nearby</p>
                  <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)' }}>will be notified once approved</p>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowConfirmation(false); setCurrentStep(1); setFormData({ title: '', dates: [''], location: '', description: '', crewCount: 1, crewMembers: [{ role: '', id: 1, dateAssignments: [{ dateIndex: 0, hours: '', ratePerHour: '' }] }], requiresWWCC: false, jobType: '' }); }}
                style={{ ...ghostBtn, flex: 1 }}>Post another</button>
              <button onClick={() => navigate('/hire-crew')}
                style={{ ...primaryBtn, flex: 1 }}>View my jobs</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostJobUltraModern;
